import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useBooking } from '../context/BookingContext';
import DatePicker from './DatePicker';
import { apiService } from '../services/apiService';
import CustomDatePicker from './CustomDatePicker';

const SearchBar = () => {
  const { state, dispatch } = useBooking();
  
  // ==========================================================
  // --- MOBILE UI STATE ---
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false); // Level 1: Main Mobile Modal
  const [isDestinationOpen, setIsDestinationOpen] = useState(false);   // Level 2: Destination Search View
  const [isGuestSheetOpen, setIsGuestSheetOpen] = useState(false);     // Level 2: Guest Bottom Sheet
  // ==========================================================

  // Desktop Toggles
  const [hotelDropdownOpen, setHotelDropdownOpen] = useState(false);
  const [guestsDropdownOpen, setGuestsDropdownOpen] = useState(false);
  
  // Data State
  const [hotelSearch, setHotelSearch] = useState('');
  const [hotels, setHotels] = useState([]);
  const [hotelsLoading, setHotelsLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);

  // --- API LOGIC ---
 const fetchHotels = useCallback(async (searchTerm = '') => {
  try {
    setHotelsLoading(true);
    const hotelData = await apiService.getHotelList(searchTerm);
    setHotels(hotelData);
    
    // Only set default hotel if we don't already have one selected
    if (hotelData.length > 0 && initialLoad && searchTerm === '') {
      // Check if we have a saved hotel in state or session storage
      const savedHotelId = state.searchState.hotelId;
      
      console.log('📋 Debug - Initial Load:', {
        initialLoad,
        searchTerm,
        hotelDataCount: hotelData.length,
        savedHotelId,
        currentHotel: state.searchState.hotel,
        currentHotelId: state.searchState.hotelId,
        hotelsList: hotelData.map(h => ({ id: h.id, name: h.name }))
      });
      
      // IMPORTANT: If we already have a hotel selected, don't change it
      if (savedHotelId && state.searchState.hotel && state.searchState.hotel !== "ABC Hotel") {
        console.log('✅ Already have a selected hotel:', state.searchState.hotel, 'keeping it');
        setInitialLoad(false);
        return; // Exit early - don't change the selection
      }
      
      if (savedHotelId) {
        // Try to find the saved hotel in the fetched list
        const savedHotel = hotelData.find(h => h.id === savedHotelId);
        console.log('🔍 Looking for saved hotel:', savedHotelId, 'Found:', savedHotel);
        
        if (savedHotel) {
          // Update state with the saved hotel
          console.log('✅ Found saved hotel, selecting:', savedHotel.name);
          dispatch({
            type: 'UPDATE_SEARCH_STATE',
            payload: { 
              hotel: savedHotel.name, 
              hotelId: savedHotel.id 
            }
          });
        } else {
          // Saved hotel not found in list, use first hotel
          console.log('❌ Saved hotel not found in list, using first hotel');
          dispatch({
            type: 'UPDATE_SEARCH_STATE',
            payload: { 
              hotel: hotelData[0].name, 
              hotelId: hotelData[0].id 
            }
          });
        }
      } else {
        // No saved hotel, use first from list
        console.log('📝 No saved hotelId, using first from list');
        dispatch({
          type: 'UPDATE_SEARCH_STATE',
          payload: { 
            hotel: hotelData[0].name, 
            hotelId: hotelData[0].id 
          }
        });
      }
      setInitialLoad(false);
    }
  } catch (err) {
    console.error(err);
  } finally {
    setHotelsLoading(false);
  }
}, [dispatch, initialLoad, state.searchState.hotelId, state.searchState.hotel]); // Add hotel to dependencies

  useEffect(() => { fetchHotels(); }, [fetchHotels]);

  // Debounce Search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if(hotelSearch || hotelDropdownOpen || isDestinationOpen) fetchHotels(hotelSearch);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [hotelSearch, hotelDropdownOpen, isDestinationOpen, fetchHotels]);

  // --- SCROLL LOCK FOR MOBILE MODALS ---
  useEffect(() => {
    const lock = isMobileSearchOpen || isDestinationOpen || isGuestSheetOpen;
    document.body.style.overflow = lock ? 'hidden' : 'auto';
    return () => { document.body.style.overflow = 'auto'; };
  }, [isMobileSearchOpen, isDestinationOpen, isGuestSheetOpen]);

  // --- HANDLERS ---
  const filteredHotels = hotels.filter(hotel =>
    hotel.name.toLowerCase().includes(hotelSearch.toLowerCase())
  );

  const handleHotelSelect = (hotel) => {
    dispatch({ type: 'UPDATE_SEARCH_STATE', payload: { hotel: hotel.name, hotelId: hotel.id } });

     // Also update session storage directly to ensure persistence
  try {
    const currentSession = JSON.parse(sessionStorage.getItem('bookingSession') || '{}');
    console.log('💾 Current session before update:', currentSession);
    
    currentSession.searchState = {
      ...currentSession.searchState,
      hotel: hotel.name,
      hotelId: hotel.id
    };
    
    sessionStorage.setItem('bookingSession', JSON.stringify(currentSession));
    console.log('💾 Session stored after update:', {
      hotel: hotel.name,
      hotelId: hotel.id,
      fullSession: currentSession
    });
  } catch (error) {
    console.error('Error updating session storage:', error);
  }

    setHotelDropdownOpen(false);
    setIsDestinationOpen(false);
    setHotelSearch('');
  };

  const handleGuestsUpdate = (rooms) => {
    dispatch({ type: 'UPDATE_ROOMS', payload: rooms });
    const totalAdults = rooms.reduce((acc, r) => acc + r.adults, 0);
    const totalChildren = rooms.reduce((acc, r) => acc + r.children, 0);
    const totalGuests = totalAdults + totalChildren; 
    const guestsText = `${rooms.length} Room${rooms.length !== 1 ? 's' : ''}, ${totalGuests} Guest${totalGuests !== 1 ? 's' : ''}`;
    
    const rooms_guests = rooms.map((room, index) => ({
      room_no: index + 1, 
    no_of_adults: room.adults, 
    no_of_children: room.children,
    // ADD THIS LINE:
    child_ages: room.childAges || [] 
  }));

    dispatch({
      type: 'UPDATE_SEARCH_STATE',
      payload: { guestsText, guestCount: totalGuests, room_guest: rooms_guests }
    });
    setGuestsDropdownOpen(false);
  };

  const handleSearch = async () => {
    console.log('🔍 handleSearch called with hotelId:', state.searchState.hotelId);
    console.trace('handleSearch stack trace');
    if (!state.searchState.hotelId || !state.searchState.checkin || !state.searchState.checkout) {
      alert('Please select hotel, check-in, and check-out dates');
      return;
    }
    const formatDate = (d) => {
      const date = new Date(d);
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    };

    const searchParams = {
      startDate: formatDate(state.searchState.checkin),
      endDate: formatDate(state.searchState.checkout),
      rooms: state.rooms.length,
      guests: state.searchState.guestCount,
      roomsData: state.rooms
    };

    try {
      setSearchLoading(true);
      const hotelData = await apiService.getHotelData(state.searchState.hotelId, searchParams);
      dispatch({
        type: 'PERFORM_SEARCH',
        payload: {
          hotelId: state.searchState.hotelId,
          checkin: state.searchState.checkin,
          checkout: state.searchState.checkout,
          rooms: state.rooms.length,
          guests: state.searchState.guestCount,
          apiResponse: hotelData
        }
      });
      
      setIsMobileSearchOpen(false);

    } catch (error) {
      console.error(error);
      alert('Search failed');
    } finally {
      setSearchLoading(false);
    }
  };

  return (
    <>
      {/* ========================================================== */}
      {/* DESKTOP VIEW                                              */}
      {/* ========================================================== */}
      <div className="hidden md:block w-full bg-white border-b border-gray-200">
        <div className="container mx-auto px-6 lg:px-8 py-3">
          <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-5 gap-x-4 gap-y-4 items-end">
            
            {/* Desktop Hotel */}
            <div className="lg:col-span-1 relative">
              <label className="block text-xs font-semibold text-text-secondary mb-1 uppercase tracking-wider">Hotel</label>
              <button
                type="button"
                onClick={() => setHotelDropdownOpen(!hotelDropdownOpen)}
                className="w-full h-10 text-left px-3 py-2 border border-gray-300 rounded-md text-sm bg-white flex items-center justify-between hover:border-brand-accent transition-colors"
              >
                <span className="truncate font-medium text-gray-900">{state.searchState.hotel || 'Select Hotel'}</span>
                <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/></svg>
              </button>
              
              {hotelDropdownOpen && (
                <div className="absolute top-full left-0 w-full z-50 bg-white border border-gray-200 rounded-md shadow-lg mt-1 animate-in fade-in zoom-in-95 duration-100">
                  <div className="p-2 border-b border-gray-100">
                    <input
                      type="text"
                      value={hotelSearch}
                      onChange={(e) => setHotelSearch(e.target.value)}
                      placeholder="Search..."
                      className="w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-brand-accent"
                      autoFocus
                    />
                  </div>
                  <div className="max-h-60 overflow-y-auto">
                    {filteredHotels.map((hotel) => (
                      <div key={hotel.id} className="p-3 text-sm cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => handleHotelSelect(hotel)}>
                        {hotel.name}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <CustomDatePicker />

            <GuestsSelector
              isOpen={guestsDropdownOpen}
              onToggle={() => setGuestsDropdownOpen(!guestsDropdownOpen)}
              onUpdate={handleGuestsUpdate}
              rooms={state.rooms}
            />

            <button onClick={handleSearch} disabled={searchLoading} className="bg-brand-accent text-white h-10 rounded-md font-bold text-sm hover:bg-brand-accent-hover w-full shadow-sm transition-all active:scale-95">
              {searchLoading ? 'Searching...' : 'Search'}
            </button>
          </div>
        </div>
      </div>

      {/* ========================================================== */}
      {/* MOBILE VIEW (Sticky Bar + Modals)                          */}
      {/* ========================================================== */}
      
    {/* 1. Sticky Top Search Bar */}
      <div className="md:hidden sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm">
        <button 
          onClick={() => setIsMobileSearchOpen(true)}
          className="w-full bg-white text-left px-4 py-3 flex items-center gap-3"
        >
          <div className="w-10 h-10 bg-brand-accent/10 rounded-full flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-brand-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-gray-900 truncate">
              {state.searchState.hotel || 'Where are you going?'}
            </div>
            <div className="text-xs text-gray-500 truncate">
              {state.searchState.checkin && state.searchState.checkout 
                ? `${new Date(state.searchState.checkin).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${new Date(state.searchState.checkout).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                : 'Add dates'
              } • {state.rooms.length} Room, {state.rooms.reduce((acc, r) => acc + r.adults, 0)} Adult, {state.rooms.reduce((acc, r) => acc + r.children, 0)} Child
            </div>
          </div>
          <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* 2. Main Mobile Modal */}
      {isMobileSearchOpen && (
        <div className="md:hidden fixed inset-0 z-50 bg-gray-50 flex flex-col animate-in slide-in-from-bottom duration-200">
          <div className="bg-white px-4 py-4 flex items-center justify-between border-b border-gray-100 shadow-sm shrink-0">
            <button onClick={() => setIsMobileSearchOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-600">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
            <h2 className="font-bold text-lg text-gray-800">Plan your trip</h2>
            <div className="w-8"></div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            
            {/* DESTINATION TRIGGER */}
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Destination</label>
              <button 
                onClick={() => setIsDestinationOpen(true)} 
                className="w-full flex items-center bg-gray-50 border border-gray-200 rounded-xl h-12 px-3 text-left"
              >
                <svg className="w-5 h-5 text-brand-accent mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/></svg>
                <span className={`text-lg font-semibold ${state.searchState.hotel ? 'text-gray-900' : 'text-gray-400'}`}>
                  {state.searchState.hotel || 'Where to?'}
                </span>
              </button>
            </div>

            {/* Dates */}
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Dates</label>
              <CustomDatePicker />
            </div>

          {/* Rooms & Guests Trigger */}
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Rooms & Guests</label>
              <button onClick={() => setIsGuestSheetOpen(true)} className="w-full h-12 flex items-center justify-between bg-gray-50 rounded-lg px-3 border border-gray-200 text-left">
                <span className="font-semibold text-gray-800">
                  {state.rooms.length} Room{state.rooms.length > 1 ? 's' : ''}, {state.rooms.reduce((acc, r) => acc + r.adults, 0)} Adult{state.rooms.reduce((acc, r) => acc + r.adults, 0) > 1 ? 's' : ''}, {state.rooms.reduce((acc, r) => acc + r.children, 0)} Child{state.rooms.reduce((acc, r) => acc + r.children, 0) !== 1 ? 'ren' : ''}
                </span>
                <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
              </button>
            </div>
            
            <div className="h-20"></div>
          </div>

          <div className="bg-white border-t border-gray-200 p-4 shrink-0 pb-safe">
            <button onClick={handleSearch} disabled={searchLoading} className="w-full bg-brand-accent h-14 rounded-xl text-white font-bold text-lg shadow-lg flex items-center justify-center gap-2">
              {searchLoading ? 'Searching...' : 'Search'}
            </button>
          </div>
        </div>
      )}

      {/* 3. DESTINATION FULL SCREEN SHEET */}
      {isDestinationOpen && (
        <div className="md:hidden fixed inset-0 z-[60] bg-white flex flex-col animate-in slide-in-from-right duration-300">
           <div className="flex items-center gap-2 p-4 border-b border-gray-100 shadow-sm shrink-0">
              <button onClick={() => setIsDestinationOpen(false)} className="p-2 rounded-full hover:bg-gray-100">
                 <svg className="w-6 h-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
              </button>
              <div className="flex-1 relative">
                 <input 
                    autoFocus
                    type="text"
                    className="w-full h-12 pl-10 pr-4 bg-gray-100 rounded-xl font-semibold text-lg outline-none focus:ring-2 focus:ring-brand-accent/50"
                    placeholder="Search hotel name..."
                    value={hotelSearch}
                    onChange={(e) => setHotelSearch(e.target.value)}
                  />
                 <svg className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
              </div>
           </div>

           <div className="flex-1 overflow-y-auto p-4">
              <div className="space-y-1">
                 {filteredHotels.length > 0 ? filteredHotels.map(h => (
                   <div 
                     key={h.id} 
                     onClick={() => handleHotelSelect(h)}
                     className="flex items-center gap-3 p-4 border-b border-gray-50 active:bg-gray-50 transition-colors"
                   >
                      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                        <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/></svg>
                      </div>
                      <div>
                          <div className="font-semibold text-gray-900 text-lg">{h.name}</div>
                          <div className="text-sm text-gray-400">Hotel</div>
                      </div>
                   </div>
                 )) : (
                   <div className="text-center py-10 text-gray-400">
                      <svg className="w-12 h-12 mx-auto mb-3 text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                      No hotels found
                   </div>
                 )}
              </div>
           </div>
        </div>
      )}

      {/* 4. GUEST BOTTOM SHEET */}
      {isGuestSheetOpen && (
        <>
          <div className="md:hidden fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm transition-opacity" onClick={() => setIsGuestSheetOpen(false)}></div>
          <div className="md:hidden fixed bottom-0 left-0 right-0 z-[70] bg-white rounded-t-3xl shadow-2xl flex flex-col max-h-[85vh] animate-in slide-in-from-bottom duration-300">
             <div className="flex items-center justify-between p-4 border-b border-gray-100">
                <h3 className="text-lg font-bold text-gray-800">Select Guests</h3>
                <button onClick={() => setIsGuestSheetOpen(false)} className="p-2 bg-gray-100 rounded-full">
                   <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
             </div>
             <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
                <GuestsSelector isOpen={true} onUpdate={handleGuestsUpdate} rooms={state.rooms} isMobile={true} />
                <div className="h-20"></div>
             </div>
             <div className="p-4 bg-white border-t border-gray-200 pb-safe">
                <button onClick={() => setIsGuestSheetOpen(false)} className="w-full h-12 bg-brand-accent text-white font-bold rounded-xl shadow-md">Done</button>
             </div>
          </div>
        </>
      )}
    </>
  );
};

// --- GUESTS COMPONENT ---
const GuestsSelector = ({ isOpen, onToggle, onUpdate, rooms, isMobile }) => {
  const [localRooms, setLocalRooms] = useState([...rooms]);
  const [expandedRoom, setExpandedRoom] = useState(0); // For Desktop: Tracks which room is being edited

  useEffect(() => { setLocalRooms([...rooms]); }, [rooms, isOpen]);

const updateRoom = (index, field, value) => {
  const newRooms = [...localRooms];
  
  // Special logic for updating child count to sync with childAges array
  if (field === 'children') {
    const currentChildren = newRooms[index].children;
    let newAges = newRooms[index].childAges ? [...newRooms[index].childAges] : [];

    if (value > currentChildren) {
      // Increasing: Add default age (0) for new child
      newAges.push(0);
    } else if (value < currentChildren) {
      // Decreasing: Remove last child's age
      newAges.pop();
    }
    
    newRooms[index] = { ...newRooms[index], children: value, childAges: newAges };
  } else {
    // Standard update for adults
    newRooms[index] = { ...newRooms[index], [field]: value };
  }

  setLocalRooms(newRooms);
  if(isMobile) onUpdate(newRooms);
};

const handleAgeChange = (roomIndex, childIndex, age) => {
  const newRooms = [...localRooms];
  const newAges = [...newRooms[roomIndex].childAges];
  newAges[childIndex] = parseInt(age);
  newRooms[roomIndex] = { ...newRooms[roomIndex], childAges: newAges };
  setLocalRooms(newRooms);
  if(isMobile) onUpdate(newRooms);
};

  const addRoom = () => {
  //  if (localRooms.length < 5) {
    // UPDATED: Initialize with childAges array
    const newRooms = [...localRooms, { adults: 1, children: 0, childAges: [] }];
    setLocalRooms(newRooms);
    setExpandedRoom(newRooms.length - 1);
    if(isMobile) onUpdate(newRooms);
  // }
};

  const removeRoom = (index) => {
    if (localRooms.length > 1) {
      const newRooms = localRooms.filter((_, i) => i !== index);
      setLocalRooms(newRooms);
      setExpandedRoom(Math.max(0, index - 1)); // Safely shift focus
      if(isMobile) onUpdate(newRooms);
    }
  };

  const handleApply = () => { onUpdate(localRooms); if(onToggle) onToggle(); };

  const totalAdults = localRooms.reduce((acc, r) => acc + r.adults, 0);
  const totalChildren = localRooms.reduce((acc, r) => acc + r.children, 0);
  const guestsText = `${localRooms.length} Room${localRooms.length !== 1 ? 's' : ''}, ${totalAdults} Adult${totalAdults !== 1 ? 's' : ''}, ${totalChildren} Child${totalChildren !== 1 ? 'ren' : ''}`;

  // === MOBILE RENDER (Stacked, Always Expanded) ===
 if (isMobile) {
    return (
      <div className="space-y-4">
        {localRooms.map((room, index) => (
          <div key={index} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex justify-between items-center mb-4 border-b border-gray-50 pb-3">
              <span className="font-bold text-gray-800">Room {index + 1}</span>
              {localRooms.length > 1 && <button onClick={() => removeRoom(index)} className="text-red-500 text-xs font-bold uppercase tracking-wider px-2 py-1 bg-red-50 rounded">Remove</button>}
            </div>
            
            {/* Adults Row */}
            <div className="flex justify-between items-center mb-4">
               <div><div className="font-semibold text-gray-700">Adults</div><div className="text-xs text-gray-400">Ages 13+</div></div>
               <div className="flex items-center gap-3"><CounterBtn onClick={() => updateRoom(index, 'adults', room.adults - 1)} disabled={room.adults <= 1} icon="-" /><span className="w-6 text-center font-bold text-lg text-gray-900">{room.adults}</span><CounterBtn onClick={() => updateRoom(index, 'adults', room.adults + 1)} disabled={room.adults >= 4} icon="+" /></div>
            </div>
            
            {/* Children Row - Updated Max Limit to 2 */}
            <div className="flex justify-between items-center">
               <div><div className="font-semibold text-gray-700">Children</div><div className="text-xs text-gray-400">Ages 0-12</div></div>
               <div className="flex items-center gap-3"><CounterBtn onClick={() => updateRoom(index, 'children', room.children - 1)} disabled={room.children <= 0} icon="-" /><span className="w-6 text-center font-bold text-lg text-gray-900">{room.children}</span><CounterBtn onClick={() => updateRoom(index, 'children', room.children + 1)} disabled={room.children >= 2} icon="+" /></div>
            </div>

            {/* Child Age Dropdowns */}
            {room.children > 0 && (
              <div className="mt-3 grid grid-cols-2 gap-3 pt-3 border-t border-gray-50">
                {Array.from({ length: room.children }).map((_, childIdx) => (
                  <div key={childIdx}>
                    <label className="block text-xs text-gray-500 mb-1">Child {childIdx + 1} Age</label>
                    <select
                      value={room.childAges?.[childIdx] || 0}
                      onChange={(e) => handleAgeChange(index, childIdx, e.target.value)}
                      className="w-full p-2 border border-gray-200 rounded-md text-sm bg-white focus:border-brand-accent outline-none"
                    >
                      {[...Array(13).keys()].map(age => (
                        <option key={age} value={age}>{age} years</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
        <button onClick={addRoom} className="w-full py-4 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 font-bold text-sm hover:border-brand-accent hover:text-brand-accent flex items-center justify-center gap-2"><svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/></svg>Add Another Room</button>
      </div>
    );
  }

  // === DESKTOP RENDER (Accordion / Summary Style) ===
  return (
    <div className="relative">
      <label htmlFor="guests-button" className="block text-xs font-semibold text-text-secondary mb-1 uppercase tracking-wider">Rooms & Guests</label>
      <button 
        type="button" 
        id="guests-button" 
        onClick={onToggle} 
        className="w-full h-10 text-left px-3 py-2 border border-gray-300 rounded-md text-sm bg-white cursor-pointer flex items-center justify-between hover:border-brand-accent transition-colors"
      >
        <span className="truncate font-medium text-gray-900">{guestsText}</span>
        <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 w-80 mt-1 bg-white border border-gray-200 rounded-md shadow-xl z-50 flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-100">
           <div className="max-h-[300px] overflow-y-auto">
             {localRooms.map((room, index) => {
               const isExpanded = expandedRoom === index;
               
    if (isExpanded) {
                  // --- RENDER ACTIVE EDITING ROOM (DESKTOP) ---
                  return (
                    <div key={index} className="p-4 border-b border-gray-100 bg-white">
                      <div className="flex justify-between items-center mb-3">
                        <span className="font-bold text-dark">Room {index + 1}</span>
                        {localRooms.length > 1 && (
                          <button onClick={() => removeRoom(index)} className="text-xs text-red-500 hover:bg-red-50 px-2 py-1 rounded">Remove</button>
                        )}
                      </div>
                      
                      {/* Adults Row */}
                      <div className="flex justify-between items-center mb-3">
                          <div className="text-sm text-gray-700">Adults <span className="text-xs text-gray-400 block">(12+ yrs)</span></div>
                          <div className="flex items-center gap-2">
                            <button className="w-7 h-7 rounded border border-gray-200 flex items-center justify-center text-dark hover:bg-gray-50 disabled:opacity-50" onClick={() => updateRoom(index, 'adults', room.adults - 1)} disabled={room.adults <= 1}>-</button>
                            <span className="w-5 text-center font-bold text-gray-800">{room.adults}</span>
                            <button className="w-7 h-7 rounded border border-gray-200 flex items-center justify-center text-dark hover:bg-gray-50" onClick={() => updateRoom(index, 'adults', room.adults + 1)} disabled={room.adults >= 4}>+</button>
                          </div>
                      </div>

                      {/* Children Row - LIMIT UPDATED TO 2 */}
                      <div className="flex justify-between items-center">
                          <div className="text-sm text-gray-700">Children <span className="text-xs text-gray-400 block">(0-11 yrs)</span></div>
                          <div className="flex items-center gap-2">
                            <button className="w-7 h-7 rounded border border-gray-200 flex items-center justify-center text-dark hover:bg-gray-50 disabled:opacity-50" onClick={() => updateRoom(index, 'children', room.children - 1)} disabled={room.children <= 0}>-</button>
                            <span className="w-5 text-center font-bold text-gray-800">{room.children}</span>
                            <button className="w-7 h-7 rounded border border-gray-200 flex items-center justify-center text-dark hover:bg-gray-50" onClick={() => updateRoom(index, 'children', room.children + 1)} disabled={room.children >= 2}>+</button>
                          </div>
                      </div>

                      {/* Child Age Dropdowns (ADDED) */}
                      {room.children > 0 && (
                        <div className="mt-3 grid grid-cols-2 gap-2 pt-2 border-t border-gray-50">
                          {Array.from({ length: room.children }).map((_, childIdx) => (
                            <div key={childIdx}>
                              <label className="block text-[10px] text-gray-500 mb-1 uppercase tracking-wide">Child {childIdx + 1} Age</label>
                              <select
                                value={room.childAges?.[childIdx] || 0}
                                onChange={(e) => handleAgeChange(index, childIdx, e.target.value)}
                                className="w-full p-1.5 border border-gray-200 rounded text-sm bg-white focus:border-brand-accent outline-none cursor-pointer"
                              >
                                {[...Array(13).keys()].map(age => (
                                  <option key={age} value={age}>{age} years</option>
                                ))}
                              </select>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                } else {
                 return (
                   <div key={index} className="p-3 border-b border-gray-100 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors">
                     <div>
                       <div className="font-bold text-xs text-gray-500 uppercase tracking-wide mb-0.5">Room {index + 1}</div>
                       <div className="text-sm font-semibold text-gray-800">
                         {room.adults} Adults, {room.children} Children
                       </div>
                     </div>
                     <div className="flex items-center gap-2">
                        <button onClick={() => setExpandedRoom(index)} className="text-brand-accent text-sm font-bold px-2 py-1 hover:bg-brand-accent/5 rounded">Edit</button>
                        {localRooms.length > 1 && (
                          <button onClick={() => removeRoom(index)} className="text-gray-400 hover:text-red-500 p-1">
                             <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                          </button>
                        )}
                     </div>
                   </div>
                 );
               }
             })}
           </div>
           
           <div className="p-3 bg-white border-t border-gray-100 flex justify-between items-center gap-3">
              
                <button onClick={addRoom} className="text-brand-accent text-sm font-bold hover:underline py-2">+ Add Room</button>
               <div />
              <button onClick={handleApply} className="bg-brand-accent-hover text-white px-6 py-2 rounded-md font-bold text-sm shadow hover:bg-brand-accent-hover transition-colors">Apply</button>
           </div>
        </div>
      )}
    </div>
  );
};

const CounterBtn = ({ onClick, disabled, icon }) => (
  <button onClick={onClick} disabled={disabled} className="w-10 h-10 rounded-full border border-gray-200 bg-white text-brand-accent flex items-center justify-center font-bold text-xl shadow-sm disabled:opacity-30 disabled:bg-gray-50 active:scale-90 transition-transform">{icon}</button>
);

export default SearchBar;